import React, { useState, useEffect } from "react";
import "./EditProductForm.css";

const EditProductForm = ({ inventory, onClose, onSave }) => {
  const permissions = JSON.parse(localStorage.getItem("permissions") || "[]");

  const [activeTab, setActiveTab] = useState("basic");
  const [departments, setDepartments] = useState([]);
  const [classifications, setClassifications] = useState([]);

  const [showInactiveModal, setShowInactiveModal] = useState(false);
  const [inactiveReasonDraft, setInactiveReasonDraft] = useState("");

  const [formData, setFormData] = useState({
    item_code: inventory.item_code || "",
    item_first_name: inventory.item_first_name || "",
    item_second_name: inventory.item_second_name || "",
    serial_number: inventory.serial_number || "",
    description: inventory.description || "",
    barcode: inventory.barcode || "",
    price: inventory.price || "",
    classification_id: inventory.classification_id || "",
    department_id: inventory.department_id || "",
    is_active:
      inventory.is_active === 0 || inventory.is_active === "0" ? 0 : 1,
    inactive_reason: inventory.inactive_reason || "",
  });

  useEffect(() => {
    fetch("/api/departments")
      .then((res) => res.json())
      .then((data) => setDepartments(Array.isArray(data) ? data : []))
      .catch((err) => console.error("Error fetching departments:", err));

    fetch("/api/asset-classifications")
      .then((res) => res.json())
      .then((data) => setClassifications(Array.isArray(data) ? data : []))
      .catch((err) => console.error("Error fetching classifications:", err));
  }, []);

  const canEdit = (permissionKey) => permissions.includes(permissionKey);

  const fieldStyle = (allowed) => ({
    backgroundColor: allowed ? "#ffffff" : "#f3f4f6",
    cursor: allowed ? "text" : "not-allowed",
  });

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleActiveToggle = () => {
    const currentlyActive = Number(formData.is_active) === 1;

    if (currentlyActive) {
      setInactiveReasonDraft(formData.inactive_reason || "");
      setShowInactiveModal(true);
      return;
    }

    // Toggle back to active immediately
    setFormData((prev) => ({
      ...prev,
      is_active: 1,
      inactive_reason: "",
    }));
  };

  const confirmInactive = () => {
    const reason = inactiveReasonDraft.trim();

    if (!reason) {
      alert("Please enter a reason before marking this item as inactive.");
      return;
    }

    setFormData((prev) => ({
      ...prev,
      is_active: 0,
      inactive_reason: reason,
    }));

    setShowInactiveModal(false);
  };

  const cancelInactive = () => {
    setInactiveReasonDraft("");
    setShowInactiveModal(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const response = await fetch(
        `/api/products/update/${encodeURIComponent(inventory.item_code)}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            ...formData,
            is_active: Number(formData.is_active),
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.error || `Failed to update inventory (${response.status})`
        );
      }

      const data = await response.json();
      console.log("✅ Update success:", data);

      onSave({
        ...inventory,
        ...formData,
        is_active: Number(formData.is_active),
      });

      onClose();
    } catch (error) {
      console.error("❌ Error updating inventory:", error);
      alert(error.message || "Failed to update inventory. Please try again.");
    }
  };

  return (
    <div className="modal-overlay">
      <div className="edit-product-modal">
        <button className="close-button" onClick={onClose}>
          ✖
        </button>

        <div className="edit-product-header">
          <div>
            <p className="edit-product-eyebrow">Inventory Management</p>
            <h3>Edit Inventory</h3>
            <p className="edit-product-subtitle">
              Update item details, classification and active status.
            </p>
          </div>

          <div className="status-toggle-wrap">
            <span
              className={
                Number(formData.is_active) === 1
                  ? "status-pill active"
                  : "status-pill inactive"
              }
            >
              {Number(formData.is_active) === 1 ? "Active" : "Inactive"}
            </span>

            <button
              type="button"
              className={
                Number(formData.is_active) === 1
                  ? "active-toggle active"
                  : "active-toggle inactive"
              }
              onClick={handleActiveToggle}
            >
              <span className="toggle-dot" />
            </button>
          </div>
        </div>

        <div className="edit-tabs">
          <button
            type="button"
            className={activeTab === "basic" ? "tab-btn active" : "tab-btn"}
            onClick={() => setActiveTab("basic")}
          >
            Basic Info
          </button>

          <button
            type="button"
            className={activeTab === "asset" ? "tab-btn active" : "tab-btn"}
            onClick={() => setActiveTab("asset")}
          >
            Asset Details
          </button>

          <button
            type="button"
            className={activeTab === "status" ? "tab-btn active" : "tab-btn"}
            onClick={() => setActiveTab("status")}
          >
            Status
          </button>
        </div>

        <form onSubmit={handleSubmit} className="edit-product-form">
          {activeTab === "basic" && (
            <div className="tab-panel">
              <div className="form-grid">
                <div className="form-field">
                  <label>Item Code</label>
                  <input
                    type="text"
                    name="item_code"
                    value={formData.item_code}
                    onChange={handleChange}
                    required
                    disabled={!canEdit("inventory_edit_itemcode")}
                    style={fieldStyle(canEdit("inventory_edit_itemcode"))}
                  />
                </div>

                <div className="form-field">
                  <label>First Name</label>
                  <input
                    type="text"
                    name="item_first_name"
                    value={formData.item_first_name}
                    onChange={handleChange}
                    required
                    disabled={!canEdit("inventory_edit_name")}
                    style={fieldStyle(canEdit("inventory_edit_name"))}
                  />
                </div>

                <div className="form-field">
                  <label>Second Name</label>
                  <input
                    type="text"
                    name="item_second_name"
                    value={formData.item_second_name}
                    onChange={handleChange}
                    disabled={!canEdit("inventory_edit_name")}
                    style={fieldStyle(canEdit("inventory_edit_name"))}
                  />
                </div>

                <div className="form-field">
                  <label>Price</label>
                  <input
                    type="number"
                    step="0.01"
                    name="price"
                    value={formData.price}
                    onChange={handleChange}
                    disabled={!canEdit("inventory_edit_price")}
                    style={fieldStyle(canEdit("inventory_edit_price"))}
                  />
                </div>

                <div className="form-field">
                  <label>Description</label>
                  <input
                    type="text"
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    disabled={!canEdit("inventory_edit_name")}
                    style={fieldStyle(canEdit("inventory_edit_name"))}
                  />
                </div>
              </div>
            </div>
          )}

          {activeTab === "asset" && (
            <div className="tab-panel">
              <div className="form-grid">
                <div className="form-field">
                  <label>Serial Number</label>
                  <input
                    type="text"
                    name="serial_number"
                    value={formData.serial_number}
                    onChange={handleChange}
                    disabled={!canEdit("inventory_edit_serialnumber")}
                    style={fieldStyle(canEdit("inventory_edit_serialnumber"))}
                  />
                </div>

                <div className="form-field">
                  <label>Barcode</label>
                  <input
                    type="text"
                    name="barcode"
                    value={formData.barcode}
                    onChange={handleChange}
                    disabled={!canEdit("inventory_edit_barcode")}
                    style={fieldStyle(canEdit("inventory_edit_barcode"))}
                  />
                </div>

                <div className="form-field">
                  <label>Asset Class</label>
                  <select
                    name="classification_id"
                    value={formData.classification_id}
                    onChange={handleChange}
                    required
                    disabled={!canEdit("inventory_edit_assetclass")}
                    style={fieldStyle(canEdit("inventory_edit_assetclass"))}
                  >
                    <option value="">Select Class</option>
                    {classifications.map((c) => (
                      <option
                        key={c.classification_id}
                        value={c.classification_id}
                      >
                        {c.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-field">
                  <label>Department</label>
                  <select
                    name="department_id"
                    value={formData.department_id}
                    onChange={handleChange}
                    required
                    disabled={!canEdit("inventory_edit_department")}
                    style={fieldStyle(canEdit("inventory_edit_department"))}
                  >
                    <option value="">Select Department</option>
                    {departments.map((d) => (
                      <option key={d.department_id} value={d.department_id}>
                        {d.department_name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          )}

          {activeTab === "status" && (
            <div className="tab-panel">
              <div className="status-card">
                <div>
                  <p className="status-card-label">Current Inventory Status</p>
                  <h4>
                    {Number(formData.is_active) === 1 ? "Active" : "Inactive"}
                  </h4>
                  <p>
                    Active items remain available for stock-in, stock-out,
                    transfer and scanning workflows.
                  </p>
                </div>

                <button
                  type="button"
                  className={
                    Number(formData.is_active) === 1
                      ? "status-action danger"
                      : "status-action success"
                  }
                  onClick={handleActiveToggle}
                >
                  {Number(formData.is_active) === 1
                    ? "Set as Inactive"
                    : "Set as Active"}
                </button>
              </div>

              {Number(formData.is_active) === 0 && (
                <div className="inactive-reason-box">
                  <label>Inactive Reason</label>
                  <textarea
                    name="inactive_reason"
                    value={formData.inactive_reason}
                    onChange={handleChange}
                    rows="4"
                    placeholder="Reason for inactive status"
                  />
                </div>
              )}
            </div>
          )}

          <div className="form-actions">
            <button type="button" className="cancel-button" onClick={onClose}>
              Cancel
            </button>

            <button type="submit" className="save-button">
              Save Changes
            </button>
          </div>
        </form>
      </div>

      {showInactiveModal && (
        <div className="confirm-overlay">
          <div className="confirm-modal">
            <h4>Mark Item as Inactive?</h4>
            <p>
              Please provide a reason before changing this inventory item to
              inactive.
            </p>

            <textarea
              value={inactiveReasonDraft}
              onChange={(e) => setInactiveReasonDraft(e.target.value)}
              rows="5"
              placeholder="Example: Damaged item, disposed, duplicate record, lost item..."
              autoFocus
            />

            <div className="confirm-actions">
              <button
                type="button"
                className="cancel-button"
                onClick={cancelInactive}
              >
                Cancel
              </button>

              <button
                type="button"
                className="danger-button"
                onClick={confirmInactive}
              >
                Confirm Inactive
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EditProductForm;